/**
 * Tabs — a proper WAI-ARIA tablist (role=tablist/tab/tabpanel, aria-selected,
 * aria-controls, roving tabindex with Left/Right/Home/End). Controlled: the caller owns
 * `value` so a tab can also be bookmarked as a search param. The active underline is a
 * single indicator that slides between tabs (design-system.md §12), and a `TabPanel`
 * fades in when its value changes.
 */
import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  count?: number | undefined;
  disabled?: boolean | undefined;
}

export interface TabsProps<T extends string> {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  /** The `id` prefix is derived; pass one to pair with an external `TabPanel`. */
  id?: string | undefined;
}

export function tabIds(baseId: string, value: string): { tab: string; panel: string } {
  return { tab: `${baseId}-tab-${value}`, panel: `${baseId}-panel-${value}` };
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  id,
  ...rest
}: TabsProps<T>): JSX.Element {
  const autoId = useId();
  const baseId = id ?? autoId;
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  // Measure the selected tab so the underline can slide to it.
  useLayoutEffect(() => {
    const list = listRef.current;
    const tab = document.getElementById(tabIds(baseId, value).tab);
    if (!list || !tab || !list.contains(tab) || tab.offsetWidth === 0) {
      setIndicator(null);
      return;
    }
    setIndicator({ left: tab.offsetLeft, width: tab.offsetWidth });
  }, [baseId, value, items]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const enabled = items.filter((t) => !t.disabled);
    const index = enabled.findIndex((t) => t.value === value);
    if (index < 0 || enabled.length === 0) return;
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % enabled.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + enabled.length) % enabled.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = enabled.length - 1;
    else return;
    event.preventDefault();
    const target = enabled[next]!;
    onChange(target.value);
    document.getElementById(tabIds(baseId, target.value).tab)?.focus();
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={rest['aria-label']}
      className={['argus-tabs', indicator ? 'argus-tabs--animated' : ''].filter(Boolean).join(' ')}
      onKeyDown={onKeyDown}
    >
      {items.map((item) => {
        const ids = tabIds(baseId, item.value);
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            id={ids.tab}
            aria-selected={selected}
            aria-controls={ids.panel}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            className="argus-tab"
            onClick={() => onChange(item.value)}
          >
            {item.label}
            {item.count !== undefined ? (
              <span className="argus-tab__count">{item.count}</span>
            ) : null}
          </button>
        );
      })}
      {indicator ? (
        <span
          className="argus-tabs__indicator"
          aria-hidden="true"
          style={{ left: indicator.left, width: indicator.width }}
        />
      ) : null}
    </div>
  );
}

export function TabPanel({
  baseId,
  value,
  children,
  className,
}: {
  baseId: string;
  value: string;
  children: ReactNode;
  className?: string | undefined;
}): JSX.Element {
  const ids = tabIds(baseId, value);
  return (
    <div
      // Re-mount on value change so the fade-in runs for each panel swap.
      key={value}
      role="tabpanel"
      id={ids.panel}
      aria-labelledby={ids.tab}
      tabIndex={0}
      className={['argus-tabpanel', className ?? ''].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}
